
import { CommonModule } from '@angular/common';
import { HttpErrorResponse, HttpEvent, HttpEventType, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { Component, inject, OnDestroy, input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { ActivatedRoute } from "@angular/router";
import { MatSelectModule } from "@angular/material/select";
import { MatTableModule } from "@angular/material/table";

import { GlobalErrorHandlerService } from '@k12/admin-services';
import { DocumentsService } from '@k12/documents';
import { AppInsightsService, SeverityLevel } from '@k12/error_handler';
import { PromptComponent, ToastMessageType, Task, TaskTypeDocumentUpload, TaskType, ComponentType, TaskAction } from '@k12/models';
import { ToastMessageService } from '@k12/notifications';
import { TasksService, TaskTypeService } from '@k12/tasks';
import { CfiFileUploadComponent } from '@k12/uploads';
import { TaskHeaderInfoComponent } from '@k12/task-view';

import { forkJoin, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-shared-document-upload',
  imports: [
    CfiFileUploadComponent,
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatSelectModule,
    MatTableModule,
    TaskHeaderInfoComponent
  ],
  standalone: true,
  styleUrl: './upload.component.scss',
  templateUrl: './upload.component.html'
})
export class UploadComponent implements OnInit, OnDestroy {
  @ViewChild(CfiFileUploadComponent) fileUploadComponent!: CfiFileUploadComponent;

  private readonly appInsightsService = inject(AppInsightsService);
  private readonly documentsService: DocumentsService = inject(DocumentsService);
  private readonly fb: FormBuilder = inject(FormBuilder);
  private readonly globalErrorHandlerService: GlobalErrorHandlerService = inject(GlobalErrorHandlerService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly taskService: TasksService = inject(TasksService);
  private readonly taskTypeService: TaskTypeService = inject(TaskTypeService);
  private readonly toastService = inject(ToastMessageService);
  private readonly unsubscribe = new Subject<void>();

  public get hasFiles(): boolean {
    return this.fileUploadComponent?.vm?.files()?.length > 0;
  }

  public container = input.required<string>();
  fileDocumentType: string = '';
  public contextItems: Array<{ label: string; value: string }> = [];
  public errorMessage: string = '';
  public form: FormGroup;
  public isLoading: boolean = true;
  public promptComponent: PromptComponent | null = null;
  public recentTaskNote: string = '';
  public task: Task | null = null;
  public taskType: TaskType;
  public taskAction: TaskAction;
  public taskTypeAction: TaskTypeDocumentUpload;

  constructor() {
    this.form = this.fb.group({
      name: [''],
      description: [''],
      sendWeeklyReminders: [true],
      notifyOnDueDate: [false],
      sendReminder: [false],
      reminderDays: [
        {
          value: null,
          disabled: false
        },
        [Validators.min(0)]
      ],
      fileTypes: [[]],
      maxFiles: [1],
      maxFileSize: [10],
      documentType: [null]
    });
  }

  public clearErrorMessage(): void {
    this.errorMessage = '';
  }

  private isDisplayableContext(val: unknown): val is string | number {
    return (typeof val === 'string' && val.trim() !== '') || typeof val === 'number';
  }

  private prettifyLabel(key: string): string {
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
  }

  private initConfigJson(task: Task, taskType: TaskTypeDocumentUpload): void {
    this.promptComponent = {
      header: task.name,
      instructions: 'Please upload your document(s) below.',
      order: 1,
      inputType: ComponentType.FileUpload,
      componentType: ComponentType.FileUpload, // Assuming this is a string representation of the enum
      label: 'Document Upload',
      inputs: [
        {
          dataType: 'file',
          inputType: ComponentType.FileUpload,
          instructions: 'Select files to upload',
          isRequired: true,
          fileSize: taskType.uploadDocumentMaxSizeMegabytes, // Set the appropriate file size limit
          fileType: taskType.uploadDocumentAcceptableFileExtensions.split(','), // Set allowed file types
          max: taskType.uploadDocumentMaxCount, // Set the maximum number of files
          order: 0,
          label: 'Files',
          options: [] // Set files and max size in code for now
        }
      ]
    };
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public ngOnInit(): void {
    const taskId = this.route.snapshot.paramMap.get('id');
    const taskActionId = this.route.snapshot.paramMap.get('typeId');

    if (!taskId || !taskActionId) {
      this.isLoading = false;
      this.toastService.showToast(ToastMessageType.Error, 'Invalid task or task action ID.');
      this.globalErrorHandlerService.handleError({ message: 'Invalid task or task action ID.' });
      return;
    }

    this.taskService
      .get(taskId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: HttpResponse<Task>) => {
          this.task = response.body;
          if (!this.task?.subTasks) {
            this.isLoading = false;
            this.toastService.showToast(ToastMessageType.Error, 'Task has no subtasks.');
            this.globalErrorHandlerService.handleError({ message: 'Task has no subtasks.' });
            return;
          }
          const foundTaskAction = this.task?.subTasks?.find(
            (subTask) => subTask.id?.toLowerCase() === taskActionId.toLowerCase()
          );
          if (!foundTaskAction) {
            this.isLoading = false;
            this.toastService.showToast(ToastMessageType.Error, 'Task action not found.');
            this.globalErrorHandlerService.handleError({ message: 'Task action not found.' });
            return;
          }
          // Convert createdDatetime to string to match TaskAction type
          this.taskAction = {
            ...foundTaskAction,
            id: foundTaskAction.id ?? '',
            description: foundTaskAction.description ?? '',
            createdDatetime:
              foundTaskAction.createdDatetime instanceof Date
                ? foundTaskAction.createdDatetime.toISOString()
                : foundTaskAction.createdDatetime,
            taskId: foundTaskAction.taskId ?? '',
            uploadDocumentMaxCount: foundTaskAction.uploadDocumentMaxCount ?? 0 // Provide a default value if undefined
          };
          if (foundTaskAction.formData) {
            try {
              const parsed = JSON.parse(foundTaskAction.formData) as Record<string, unknown>;
              this.contextItems = Object.entries(parsed)
                .filter(([, val]) => this.isDisplayableContext(val))
                .map(([key, val]) => ({ label: this.prettifyLabel(key), value: String(val) }));
            } catch {
              /* no-op: uploads without formData context */
            }
          }
          this.getTaskTypes(taskId);
        },
        error: (error: unknown) => {
          this.isLoading = false;
          const msg: string = (error as HttpErrorResponse)?.error?.message || 'Loading Task details failed.';
          this.appInsightsService.log(new Error(msg), SeverityLevel.error);
          this.toastService.showToast(ToastMessageType.Error, msg);
          this.globalErrorHandlerService.handleError(error);
        }
      });
  }

  public onBack(): void {
    globalThis.history.back();
  }

  public onFilesAdded(files: File[]): void {
    // Clear any existing error message when files are successfully added
    if (files && files.length > 0) {
      this.clearErrorMessage();
    }
  }

  public onSubmit(): void {
    if (!this.hasFiles || !this.taskAction) {
      return;
    }

    const collectionType = this.taskAction.id;
    const files = this.fileUploadComponent.vm.files();

    // Set uploading state
    this.fileUploadComponent.setUploadingState(true);
    this.isLoading = true;

    let totalProgress = 0;
    const totalFiles = files.length;

    // Track overall progress across all files
    const updateOverallProgress = () => {
      const overallProgress = totalProgress / totalFiles;
      this.fileUploadComponent.setUploadProgress(Math.round(overallProgress));
    };

    // Create upload observables with progress tracking
    const uploadObservables = files.map((file) => {
      return (
        this.documentsService.upload(
          file,
          this.container(),
          file.name,
          collectionType,
          this.fileDocumentType
        ) as import('rxjs').Observable<HttpEvent<unknown>>
      ).pipe(
        map((event: HttpEvent<unknown>) => {
          if (event.type === HttpEventType.UploadProgress) {
            const progress = Math.round((100 * event.loaded) / event.total!);
            // Update this file's progress in the total
            totalProgress = totalProgress - totalProgress / totalFiles + progress;
            updateOverallProgress();
          }
          return event;
        }),
        filter((event: HttpEvent<unknown>) => event.type === HttpEventType.Response),
        map((event) => event)
      );
    });

    // Upload all files concurrently
    forkJoin(uploadObservables)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: () => {
          this.fileUploadComponent.setUploadingState(false);
          if (this.task && this.taskAction) {
            const subTask = this.task.subTasks?.find((st) => st.id?.toLowerCase() === this.taskAction.id.toLowerCase());
            if (subTask) {
              subTask.taskStatusId = 2;
            }
            this.taskAction.taskStatusId = 2;
          }

          this.taskService
            .updateTask(this.task)
            .then(() => {
              this.toastService.showToast(ToastMessageType.Success, 'Document(s) have been uploaded successfully.');
              this.onBack();
            })
            .catch(() => {
              this.toastService.showToast(ToastMessageType.Error, 'Error updating task status.');
            });

          this.isLoading = false;
          this.clearErrorMessage();
        },
        error: (error: unknown) => {
          this.fileUploadComponent.setUploadingState(false);
          this.isLoading = false;
          console.error('Error uploading files:', error);
          this.errorMessage = (error as HttpErrorResponse)?.error?.message || 'Error uploading files: ';
          // Display user-friendly error message
          if ((error as HttpErrorResponse).status === HttpStatusCode.NotFound) {
            this.errorMessage += 'The upload service is not available. Please try again later.';
          } else if ((error as HttpErrorResponse).status === HttpStatusCode.PayloadTooLarge) {
            this.errorMessage += 'One or more files are too large.';
          } else if ((error as HttpErrorResponse).status === 0) {
            this.errorMessage += 'Network error. Please check your internet connection.';
          } else {
            this.errorMessage += `Server error (${(error as HttpErrorResponse).status}). Please try again.`;
          }

          // Also use the global error handler
          this.globalErrorHandlerService.handleError({ message: this.errorMessage });
          this.toastService.showToast(ToastMessageType.Error, this.errorMessage);
          this.appInsightsService.log(new Error(this.errorMessage), SeverityLevel.error);
        }
      });
  }

  private getDocumentType(): void {
    this.documentsService.getFileDocumentType(this.task?.programPartnerTypeId).subscribe({
      next: (documentTypes) => {
        const matchingDocumentType = documentTypes.find(
          (dt) => dt.documentTypeId === this.taskTypeAction.documentTypeId
        );
        if (matchingDocumentType) {
          this.fileDocumentType = matchingDocumentType.name;
        }
      },
      error: (error: { error?: { message?: string } }) => {
        const errorMessage = error?.error?.message || 'Failed to load document types.';
        this.appInsightsService.log(new Error(errorMessage), SeverityLevel.error);
        this.toastService.showToast(ToastMessageType.Error, errorMessage);
      }
    });
  }

  private getTaskTypes(taskId: string): void {
    this.taskTypeService
      .get(this.taskAction.taskTypeId ?? '')
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (taskType) => {
          if (!taskType.body) {
            this.task = null;
            this.toastService.showToast(ToastMessageType.Error, 'Task type not found.');
            this.globalErrorHandlerService.handleError({ message: 'Task type not found.' });
            this.isLoading = false;
            return;
          }
          this.taskType = taskType.body;
          const foundTaskTypeAction = this.taskType.subTasks?.find(
            (subTask) => subTask.actionId?.toLowerCase() === this.taskAction.taskTypeSubTaskId.toLowerCase()
          );
          if (!foundTaskTypeAction) {
            this.task = null;
            this.toastService.showToast(ToastMessageType.Error, 'Task type action not found.');
            this.globalErrorHandlerService.handleError({ message: 'Task type action not found.' });
            this.isLoading = false;
            return;
          }
          this.taskTypeAction = foundTaskTypeAction as TaskTypeDocumentUpload;
          this.getDocumentType();
          // make sure this is an upload task type
          if (this.task && this.taskTypeAction.$type === 'Upload') {
            this.taskService.getTaskNotes(taskId).subscribe({
              next: (resp) => {
                const apiNotes = resp?.body ?? [];
                this.recentTaskNote = apiNotes.find((note) => note.taskNoteType === 1)?.comments || '';
                if (this.task) this.initConfigJson(this.task, this.taskTypeAction);
              },
              error: (error: unknown) => {
                const msg: string = (error as HttpErrorResponse)?.error?.message || 'Failed to load task notes.';
                this.appInsightsService.log(new Error(msg), SeverityLevel.error);
                this.toastService.showToast(ToastMessageType.Error, msg);
                this.globalErrorHandlerService.handleError(error);
              }
            });
          } else {
            this.task = null;
            const msg = 'This is not an upload task template';
            this.toastService.showToast(ToastMessageType.Error, msg);
            this.appInsightsService.log(new Error(msg), SeverityLevel.error);
            this.globalErrorHandlerService.handleError({ message: msg });
          }
          this.isLoading = false;
        },
        error: (error: unknown) => {
          this.isLoading = false;
          const msg: string = (error as HttpErrorResponse)?.error?.message || 'Task Upload failed.';
          this.toastService.showToast(ToastMessageType.Error, msg);
          this.appInsightsService.log(new Error(msg), SeverityLevel.error);
          this.globalErrorHandlerService.handleError(error);
        }
      });
  }
}
